/**
 * PostgREST silently caps un-ranged selects at `max-rows` (1000 — see
 * supabase/config.toml [api] and the hosted project default). Any reader that
 * aggregates a whole table client-side (the stock ledger above all) MUST page
 * through `.range()` or it computes on a truncated result set with no error.
 *
 * PAGE_SIZE must never exceed the server's max-rows: the end-of-data signal is
 * a page shorter than the requested span.
 */
export const POSTGREST_PAGE_SIZE = 1000;

interface PageResult<T> {
  data: T[] | null;
  error: unknown;
}

/**
 * Fetch every row of a query by requesting successive `.range()` pages.
 * `page` must build a FRESH query for each call (supabase-js builders are
 * mutable) and apply a stable `.order()` so pages don't overlap.
 */
export async function fetchAllRows<T>(
  page: (from: number, to: number) => PromiseLike<PageResult<T>>,
): Promise<T[]> {
  const rows: T[] = [];
  for (let from = 0; ; from += POSTGREST_PAGE_SIZE) {
    const { data, error } = await page(from, from + POSTGREST_PAGE_SIZE - 1);
    if (error) throw error;
    const batch = data ?? [];
    rows.push(...batch);
    if (batch.length < POSTGREST_PAGE_SIZE) return rows;
  }
}

/**
 * Completes an already-issued un-ranged select when it exactly hit the
 * PostgREST cap. This preserves the cheap single request for normal tenants
 * while making the no-limit repository contract truthful for larger ones.
 */
export async function completeCappedRows<T>(
  firstPage: T[],
  page: (from: number, to: number) => PromiseLike<PageResult<T>>,
): Promise<T[]> {
  if (firstPage.length < POSTGREST_PAGE_SIZE) return firstPage;
  const tail = await fetchAllRows((from, to) =>
    page(from + POSTGREST_PAGE_SIZE, to + POSTGREST_PAGE_SIZE),
  );
  return firstPage.concat(tail);
}

/**
 * Append the primary key as the LAST sort key, making any `.order()` chain a
 * TOTAL order.
 *
 * `ORDER BY doc_date DESC, created_at DESC` looks deterministic but isn't: a
 * legacy import writes every row in one transaction, and `created_at` defaults
 * to the TRANSACTION timestamp — so hundreds of rows share the exact same sort
 * key. Postgres is then free to return tied rows in a different order for every
 * query, and `.range()` paging (OFFSET/LIMIT) serves some of them twice while
 * skipping others entirely. Measured on the biggest tenant's receptions: 1032
 * of 1243 rows sit in a tie (groups up to 13), and walking 4 pages of 100
 * returned 400 rows for 397 distinct documents.
 *
 * A row served twice reaches React under the same `key`, which orphans a `<tr>`
 * in the DOM for good (React keeps only the LAST fiber per key, so the earlier
 * duplicate is never unmounted); a row skipped is simply invisible in the list.
 *
 * `id` is unique, so ordering by it LAST fixes both without changing any
 * visible ordering.
 */
export function withStableOrder<TQuery>(query: TQuery): TQuery {
  // `any`: supabase-js query builders have no shared public interface to type
  // against — the repositories already pass them around as `any`.
  return (query as any).order('id', { ascending: false }) as TQuery;
}

/**
 * L'ordre par défaut de TOUTE liste de documents : le plus récemment CRÉÉ
 * d'abord.
 *
 * `doc_date` est saisissable (et souvent antidatée : on encaisse aujourd'hui
 * une facture datée du mois dernier), donc elle ne dit pas ce qui vient d'être
 * enregistré — un document créé à l'instant se retrouvait enfoui au milieu de
 * la liste. `created_at`, lui, ne bouge jamais.
 *
 * ⚠ `doc_date` reste en SECONDE clé, et ce n'est pas décoratif : une reprise de
 * données écrit toutes les lignes dans UNE transaction, donc elles partagent
 * la même `created_at` (mesuré : 12 225 des 12 722 documents de vente du plus
 * gros tenant). Sans cette retombée, tout l'historique repris se trierait sur
 * l'`id` de `withStableOrder` — c'est-à-dire au hasard des UUID.
 */
export function compareDocumentsNewestFirst(
  a: { createdAt: string; docDate: string },
  b: { createdAt: string; docDate: string },
): number {
  return (
    (b.createdAt ?? '').localeCompare(a.createdAt ?? '') ||
    (b.docDate ?? '').localeCompare(a.docDate ?? '')
  );
}

/** Split ids into slices small enough for a PostgREST `in=(...)` URL filter. */
export function chunkIds(ids: string[], size = 200): string[][] {
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += size) {
    chunks.push(ids.slice(i, i + size));
  }
  return chunks;
}
